import { useParams, Link } from "react-router-dom";
import { useLang } from "../context/LanguageContext";
import { useScrollReveal } from "../hooks/useScrollReveal";
import usePageSeo from "../hooks/usePageSeo";
import { projectsData } from "./projectsData";
import "./ProjectDetail.css";

export default function ProjectDetail() {
  useScrollReveal();
  const { id } = useParams();
  const { t, lang } = useLang();
  const project = projectsData.find((p) => p.id === id);

  usePageSeo({
    lang,
    path: project ? `/projects/${project.id}` : "/projects",
    title: project
      ? `${lang === "IT" ? project.title_IT : project.title_EN} | GDM Installations`
      : (lang === "IT" ? "Progetto non trovato | GDM Installations" : "Project not found | GDM Installations"),
    description: project
      ? (lang === "IT" ? project.desc_IT : project.desc_EN)
      : (lang === "IT"
        ? "La pagina del progetto richiesto non e disponibile. Scopri gli altri progetti GDM Installations."
        : "The requested project page is not available. Explore other GDM Installations projects."),
    image: project ? project.imgLarge : undefined,
    robots: project ? "index, follow" : "noindex, follow",
    keywords: project
      ? `${lang === "IT" ? project.title_IT : project.title_EN}, GDM Installations, ${lang === "IT" ? project.materials_IT.join(", ") : project.materials_EN.join(", ")}`
      : undefined,
    jsonLd: project
      ? {
          "@context": "https://schema.org",
          "@type": "CreativeWork",
          name: lang === "IT" ? project.title_IT : project.title_EN,
          description: lang === "IT" ? project.desc_IT : project.desc_EN,
          url: `https://gdminstallations.it/projects/${project.id}`,
          image: project.imgLarge,
          creator: {
            "@type": "Organization",
            name: "GDM Installations",
          },
          material: lang === "IT" ? project.materials_IT : project.materials_EN,
        }
      : {
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: lang === "IT" ? "Progetto non trovato" : "Project not found",
          url: "https://gdminstallations.it/projects",
        },
  });

  if (!project) {
    return (
      <div className="project-not-found section">
        <div className="section-inner" style={{ textAlign: "center" }}>
          <p style={{ color: "var(--text-muted)", fontSize: "1.2rem" }}>Project not found.</p>
          <Link to="/projects" className="btn-outline" style={{ marginTop: "2rem", display: "inline-block" }}>
            {t.projects.backToProjects}
          </Link>
        </div>
      </div>
    );
  }

  const title = lang === "IT" ? project.title_IT : project.title_EN;
  const desc = lang === "IT" ? project.desc_IT : project.desc_EN;
  const materials = lang === "IT" ? project.materials_IT : project.materials_EN;

  return (
    <div className="project-detail-page">
      <div className="project-detail-hero" style={{ backgroundImage: `url(${project.imgLarge})` }}>
        <div className="project-detail-hero-overlay" />
        <div className="project-detail-hero-content">
          <Link to="/projects" className="back-link">{t.projects.backToProjects}</Link>
          <h1 className="project-detail-title fade-in">{title}</h1>
        </div>
      </div>

      <section className="project-detail-content section">
        <div className="section-inner project-detail-inner">
          <div className="project-detail-text fade-in">
            <div className="gold-line-left" />
            <p>{desc}</p>
          </div>
          <div className="project-detail-meta fade-in fade-in-delay-2">
            <h3 className="meta-title">{t.projects.materials}</h3>
            <div className="gold-line-left" style={{ width: "40px" }} />
            <ul className="materials-list">
              {materials.map((m) => (
                <li key={m}><span className="material-dot" />{m}</li>
              ))}
            </ul>
            <Link to="/contact" className="btn-gold" style={{ marginTop: "2rem", display: "inline-block" }}>
              {t.projects.requestSimilar}
            </Link>
          </div>
        </div>
      </section>

      <section className="related-projects section" style={{ borderTop: "1px solid var(--border-gold)" }}>
        <div className="section-inner">
          <h2 className="section-title fade-in">{t.projects.otherProjects}</h2>
          <div className="gold-line" />
          <div className="related-grid fade-in fade-in-delay-1">
            {projectsData.filter((p) => p.id !== id).slice(0, 3).map((p) => (
              <Link to={`/projects/${p.id}`} key={p.id} className="related-card">
                <img src={p.img} alt={lang === "IT" ? p.title_IT : p.title_EN} />
                <div className="related-overlay">
                  <span>{lang === "IT" ? p.title_IT : p.title_EN}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
